import React, { useMemo, useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Game } from '../../types';
import { Trophy, RefreshCw, ChevronRight } from 'lucide-react';
import { fetchTournamentTeams, generateR16Brackets, generateQFBrackets, TournamentTeam } from '../../services/tournamentService';
import { getTeamName } from '../../services/teamService';

interface Team {
    id: string;
    name: string;
    logo: string;
    score?: number;
    score1?: number; // 1차전 점수
    scoreAway1?: number; // 1차전 상대 점수
    score2?: number; // 2차전 점수
    scoreAway2?: number; // 2차전 상대 점수
    penalty?: number; // 승부차기 점수
}

interface VirtualGame extends Game {
    id1?: string;
    id2?: string;
    dateStr1?: string;
    dateStr2?: string;
    score1?: number;
    scoreAway1?: number;
    score2?: number;
    scoreAway2?: number;
    penalty?: number;
    penaltyAway?: number;
}

interface BracketProps {
    leagueId: string;
    season: string;
    fixtures: Game[];
    initialRound?: 'r32' | 'r16' | 'qf' | 'sf' | 'final';
}

export const BracketView: React.FC<BracketProps> = ({ leagueId, season, fixtures, initialRound }) => {
    const { lang } = useLanguage();

    const isWorldCup = leagueId === '1';
    const isUcl = leagueId === '2'; 
    const isUel = leagueId === '3';
    
    let trophyImg = "/assets/ball.png";
    if (isUcl) trophyImg = "/assets/trophy_ucl.avif";
    else if (isUel) trophyImg = "/assets/trophy_uel.avif";
    else if (isWorldCup) trophyImg = "/assets/trophy_fiwc.png";

    const getTeamData = (game: Game | undefined, type: 'home' | 'away'): Team | undefined => {
        if (!game) return undefined;
        const team = type === 'home' ? game.homeTeam : game.awayTeam;
        const score = game.score ? parseInt(game.score.split(':')[type === 'home' ? 0 : 1]) : undefined;
        
        // Extended score info for display
        const typedGame = game as any;
        return {
            id: team.id || '',
            name: team.name || '',
            logo: team.logo || '',
            score,
            score1: typedGame.score1 !== undefined ? (type === 'home' ? typedGame.score1 : typedGame.scoreAway1) : undefined,
            scoreAway1: typedGame.score1 !== undefined ? (type === 'home' ? typedGame.scoreAway1 : typedGame.score1) : undefined,
            score2: typedGame.score2 !== undefined ? (type === 'home' ? typedGame.score2 : typedGame.scoreAway2) : undefined,
            scoreAway2: typedGame.score2 !== undefined ? (type === 'home' ? typedGame.scoreAway2 : typedGame.score2) : undefined,
            penalty: typedGame.penalty !== undefined ? (type === 'home' ? typedGame.penalty : typedGame.penaltyAway) : undefined
        };
    };

    const [simulatedR16, setSimulatedR16] = useState<{ home: TournamentTeam, away: TournamentTeam }[]>([]);
    const [simulatedQF, setSimulatedQF] = useState<{ home: TournamentTeam, away: TournamentTeam }[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [wcRound, setWcRound] = useState<'r32' | 'r16' | 'qf' | 'sf' | 'final'>(initialRound ?? 'r32');

    useEffect(() => {
        if (isUcl && simulatedR16.length === 0) {
            generateSimulatedBrackets();
        }
    }, [isUcl, leagueId, season]);

    const generateSimulatedBrackets = async () => {
        setIsLoading(true);
        try {
            const teams = await fetchTournamentTeams(leagueId, season);
            if (teams.length >= 16) {
                const winners = teams.filter(t => t.rank === 1);
                const runnersUp = teams.filter(t => t.rank === 2);
                const pairings = generateR16Brackets(winners, runnersUp);
                setSimulatedR16(pairings);

                const r16Winners = pairings.map(p => p.home);
                const qfPairings = generateQFBrackets(r16Winners);
                setSimulatedQF(qfPairings);
            }
        } catch (error) {
            console.error("Simulation error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const bracketData = useMemo(() => {
        const groupedFixtures = fixtures.reduce((acc, f) => {
            const teamKey = [f.homeTeam.id, f.awayTeam.id].sort().join('-');
            const roundKey = f.round?.toLowerCase() || 'unknown';
            const key = `${roundKey}-${teamKey}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(f);
            return acc;
        }, {} as Record<string, Game[]>);

        const uniqueFixtures = Object.values(groupedFixtures).map(group => {
            if (group.length <= 1) {
                const singleGame = group[0];
                const singleAny = singleGame as any;
                if (singleAny.penalty) {
                    const vg = { ...singleGame } as VirtualGame;
                    const pScores = singleAny.penalty.split(':').map((s: string) => parseInt(s));
                    vg.penalty = pScores[0];
                    vg.penaltyAway = pScores[1];
                    return vg;
                }
                return singleGame;
            }
            const sorted = group.sort((a, b) => {
                const dateA = new Date(`${a.dateLabel}T${a.time}`).getTime();
                const dateB = new Date(`${b.dateLabel}T${b.time}`).getTime();
                return dateA - dateB;
            });

            const firstLeg = sorted[0];
            const latestLeg = sorted[sorted.length - 1];
            const resultGame = { ...firstLeg } as VirtualGame;
            
            resultGame.id1 = firstLeg.id;
            resultGame.dateStr1 = `${firstLeg.dateLabel.split('-').slice(1).join('.')} ${firstLeg.time}`;
            const firstScores = firstLeg.score?.split(':').map(s => parseInt(s)) || [0, 0];
            resultGame.score1 = firstScores[0];
            resultGame.scoreAway1 = firstScores[1];

            if (group.length > 1) {
                const secondLeg = sorted[1];
                resultGame.id2 = secondLeg.id;
                resultGame.dateStr2 = `${secondLeg.dateLabel.split('-').slice(1).join('.')} ${secondLeg.time}`;
                const secondScores = secondLeg.score?.split(':').map(s => parseInt(s)) || [0, 0];
                if (secondLeg.homeTeam.id === firstLeg.homeTeam.id) {
                    resultGame.score2 = secondScores[0];
                    resultGame.scoreAway2 = secondScores[1];
                } else {
                    resultGame.score2 = secondScores[1];
                    resultGame.scoreAway2 = secondScores[0];
                }
            }

            const latestLegAny = latestLeg as any;
            if (latestLegAny.penalty) {
                const pScores = latestLegAny.penalty.split(':').map((s: string) => parseInt(s));
                if (latestLegAny.homeTeam.id === firstLeg.homeTeam.id) {
                    resultGame.penalty = pScores[0];
                    resultGame.penaltyAway = pScores[1];
                } else {
                    resultGame.penalty = pScores[1];
                    resultGame.penaltyAway = pScores[0];
                }
            }

            let aggHome = 0;
            let aggAway = 0;
            group.forEach(g => {
                if (g.status === 'Finished' && g.score) {
                    const s = g.score.split(':').map(val => parseInt(val));
                    if (g.homeTeam.id === firstLeg.homeTeam.id) {
                        aggHome += s[0]; aggAway += s[1];
                    } else {
                        aggHome += s[1]; aggAway += s[0];
                    }
                }
            });
            resultGame.score = `${aggHome}:${aggAway}`;

            if (firstLeg.status !== 'Finished') return firstLeg;
            return resultGame;
        });

        const getMatchesByRound = (pattern: string, excludePatterns: string[] = []) => {
            const matches = uniqueFixtures.filter(f => {
                const roundName = f.round?.toLowerCase() || '';
                
                // 8강, 4강, 결승전 판단용 정규식
                const isFinal = /^final$|^the final$|^finals$/i.test(roundName);
                const isSF = /semi-final|semi final|semifinal/i.test(roundName);
                const isQF = /quarter-final|quarter final|quarterfinal/i.test(roundName);
                const isR16 = /round of 16|1\/8/i.test(roundName);
                const isR32 = /round of 32|1\/16/i.test(roundName);

                if (pattern === 'final') return isFinal;
                if (pattern === 'semi-finals') return isSF;
                if (pattern === 'quarter-finals') return isQF;
                if (pattern === 'round of 16') return isR16;
                if (pattern === 'round of 32') return isR32;

                // 기본 포함 여부 (기타 라운드용)
                const matchesPattern = roundName.includes(pattern.toLowerCase());
                const matchesExclude = excludePatterns.some(p => roundName.includes(p.toLowerCase()));
                return matchesPattern && !matchesExclude;
            });

            return matches.sort((a, b) => {
                const dateA = new Date(`${a.dateLabel}T${a.time}`).getTime();
                const dateB = new Date(`${b.dateLabel}T${b.time}`).getTime();
                if (dateA !== dateB) return dateA - dateB;
                return a.homeTeam.name.localeCompare(b.homeTeam.name);
            });
        };

        let r32Matches = isWorldCup ? getMatchesByRound('round of 32') : [];
        let r16Matches = getMatchesByRound('round of 16');
        if (r16Matches.length === 0 && simulatedR16.length > 0) {
            r16Matches = simulatedR16.map((p, i) => ({
                id: `sim-${i}`, league: leagueId,
                homeTeam: { name: p.home.name, logo: p.home.logo, id: p.home.id },
                awayTeam: { name: p.away.name, logo: p.away.logo, id: p.away.id },
                status: 'Scheduled', time: 'TBD', dateLabel: 'Simulation', round: 'Round of 16'
            } as Game));
        }

        let qfMatches = getMatchesByRound('quarter-finals');
        if (qfMatches.length === 0 && simulatedQF.length > 0) {
            qfMatches = simulatedQF.map((p, i) => ({
                id: `sim-qf-${i}`, league: leagueId,
                homeTeam: { name: p.home.name, logo: p.home.logo, id: p.home.id },
                awayTeam: { name: p.away.name, logo: p.away.logo, id: p.away.id },
                status: 'Scheduled', time: 'TBD', dateLabel: 'Simulation', round: 'Quarter-finals'
            } as Game));
        }

        let sfMatches = getMatchesByRound('semi-finals');
        const finalMatch = uniqueFixtures.find(f => {
            const roundName = f.round?.toLowerCase() || '';
            return /^final$|^the final$|^finals$/i.test(roundName);
        });
        const thirdPlaceMatch = isWorldCup ? uniqueFixtures.find(f => {
            const roundName = f.round?.toLowerCase() || '';
            return /3rd.?place|third.?place|bronze|play.?off for third/i.test(roundName);
        }) : undefined;

        // Reordering logic
        if (finalMatch && sfMatches.length > 0) {
            const finalTeams = [finalMatch.homeTeam.id, finalMatch.awayTeam.id];
            const reorderedSF: (Game | undefined)[] = new Array(2).fill(undefined);
            const usedSFIndices = new Set<number>();
            sfMatches.forEach((sf, idx) => {
                if (finalTeams.includes(sf.homeTeam.id) || finalTeams.includes(sf.awayTeam.id)) {
                    if (reorderedSF[0] === undefined) { reorderedSF[0] = sf; usedSFIndices.add(idx); }
                    else { reorderedSF[1] = sf; usedSFIndices.add(idx); }
                }
            });
            sfMatches.forEach((sf, idx) => { if (!usedSFIndices.has(idx)) {
                if (reorderedSF[0] === undefined) reorderedSF[0] = sf;
                else if (reorderedSF[1] === undefined) reorderedSF[1] = sf;
            }});
            sfMatches = reorderedSF.filter(m => m !== undefined) as Game[];
        }

        if (sfMatches.length > 0 && qfMatches.length > 0) {
            const reorderedQF: (Game | undefined)[] = new Array(4).fill(undefined);
            const usedQFIndices = new Set<number>();
            sfMatches.forEach((sf, i) => {
                const sfTeams = [sf.homeTeam.id, sf.awayTeam.id];
                const relatedQFIndices = qfMatches.reduce((acc, qf, idx) => {
                    if (sfTeams.includes(qf.homeTeam.id) || sfTeams.includes(qf.awayTeam.id)) acc.push(idx);
                    return acc;
                }, [] as number[]);
                if (relatedQFIndices.length > 0) {
                    if (relatedQFIndices[0] !== undefined) { reorderedQF[2 * i] = qfMatches[relatedQFIndices[0]]; usedQFIndices.add(relatedQFIndices[0]); }
                    if (relatedQFIndices[1] !== undefined) { reorderedQF[2 * i + 1] = qfMatches[relatedQFIndices[1]]; usedQFIndices.add(relatedQFIndices[1]); }
                }
            });
            qfMatches.forEach((qf, idx) => { if (!usedQFIndices.has(idx)) {
                const emptyIdx = reorderedQF.indexOf(undefined);
                if (emptyIdx !== -1) reorderedQF[emptyIdx] = qf;
            }});
            qfMatches = reorderedQF.filter(m => m !== undefined) as Game[];
        }

        if (qfMatches.length > 0 && r16Matches.length > 0) {
            const reorderedR16: (Game | undefined)[] = new Array(8).fill(undefined);
            const usedR16Indices = new Set<number>();
            qfMatches.forEach((qf, i) => {
                const qfTeams = [qf.homeTeam.id, qf.awayTeam.id];
                const relatedR16Indices = r16Matches.reduce((acc, r16, idx) => {
                    if (qfTeams.includes(r16.homeTeam.id) || qfTeams.includes(r16.awayTeam.id)) acc.push(idx);
                    return acc;
                }, [] as number[]);
                if (relatedR16Indices.length > 0) {
                    if (relatedR16Indices[0] !== undefined) { reorderedR16[2 * i] = r16Matches[relatedR16Indices[0]]; usedR16Indices.add(relatedR16Indices[0]); }
                    if (relatedR16Indices[1] !== undefined) { reorderedR16[2 * i + 1] = r16Matches[relatedR16Indices[1]]; usedR16Indices.add(relatedR16Indices[1]); }
                }
            });
            r16Matches.forEach((r16, idx) => { if (!usedR16Indices.has(idx)) {
                const emptyIdx = reorderedR16.indexOf(undefined);
                if (emptyIdx !== -1) reorderedR16[emptyIdx] = r16;
            }});
            r16Matches = reorderedR16.filter(m => m !== undefined) as Game[];
        }

        if (isWorldCup && r16Matches.length > 0 && r32Matches.length > 0) {
            const reorderedR32: (Game | undefined)[] = new Array(16).fill(undefined);
            const usedR32Indices = new Set<number>();
            r16Matches.forEach((r16, i) => {
                const r16Teams = [r16.homeTeam.id, r16.awayTeam.id];
                const related = r32Matches.reduce((acc, r32g, idx) => {
                    if (r16Teams.includes(r32g.homeTeam.id) || r16Teams.includes(r32g.awayTeam.id)) acc.push(idx);
                    return acc;
                }, [] as number[]);
                if (related[0] !== undefined) { reorderedR32[2 * i] = r32Matches[related[0]]; usedR32Indices.add(related[0]); }
                if (related[1] !== undefined) { reorderedR32[2 * i + 1] = r32Matches[related[1]]; usedR32Indices.add(related[1]); }
            });
            r32Matches.forEach((g, idx) => {
                if (!usedR32Indices.has(idx)) {
                    const emptyIdx = reorderedR32.indexOf(undefined);
                    if (emptyIdx !== -1) reorderedR32[emptyIdx] = g;
                }
            });
            r32Matches = reorderedR32.filter(Boolean) as Game[];
        }

        return {
            r32: r32Matches,
            r16: [r16Matches[0], r16Matches[1], r16Matches[2], r16Matches[3], r16Matches[4], r16Matches[5], r16Matches[6], r16Matches[7]],
            qf: [qfMatches[0], qfMatches[1], qfMatches[2], qfMatches[3]],
            sf: [sfMatches[0], sfMatches[1]],
            final: finalMatch,
            thirdPlace: thirdPlaceMatch,
        };
    }, [fixtures, simulatedR16, simulatedQF, leagueId]);

    const getWinner = (game: Game | undefined): Team | undefined => {
        if (!game || game.status !== 'Finished') return undefined;
        const t1 = getTeamData(game, 'home');
        const t2 = getTeamData(game, 'away');
        if (!t1 || !t2) return undefined;
        const s1 = t1.score || 0, s2 = t2.score || 0;
        const p1 = t1.penalty || 0, p2 = t2.penalty || 0;
        if (s1 > s2) return t1;
        if (s2 > s1) return t2;
        if (p1 > p2) return t1;
        if (p2 > p1) return t2;
        return undefined;
    };

    const MatchCard = ({ game }: { game?: Game }) => {
        const vGame = game as VirtualGame;
        const team1 = getTeamData(game, 'home');
        const team2 = getTeamData(game, 'away');
        const statusShort = game?.statusShort;
        const isPEN = statusShort === 'PEN';
        const isAET = statusShort === 'AET';

        let winnerId: string | undefined = undefined;
        if (game?.status === 'Finished' && team1 && team2) {
            const s1 = team1.score || 0;
            const s2 = team2.score || 0;
            const p1 = team1.penalty || 0;
            const p2 = team2.penalty || 0;
            if (s1 > s2) winnerId = team1.id;
            else if (s2 > s1) winnerId = team2.id;
            else if (p1 > p2) winnerId = team1.id;
            else if (p2 > p1) winnerId = team2.id;
        }

        const isSimulation = game?.id.startsWith('sim-');
        const dateStr = game ? `${game.dateLabel.split('-').slice(1).join('.')} ${game.time}` : 'TBD';

        const handleLegClick = (e: React.MouseEvent, id?: string) => {
            e.stopPropagation();
            if (id && !isSimulation) window.location.href = `/game/${id}`;
        };

        const handleCardClick = () => {
            if (game && !isSimulation) window.location.href = `/game/${vGame.id2 || game.id}`;
        };

        return (
            <div 
                onClick={handleCardClick}
                className={`relative flex flex-col w-[170px] bg-white rounded-2xl shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_10px_20px_-2px_rgba(0,0,0,0.04)] border border-slate-100 overflow-hidden group transition-all duration-500 hover:shadow-blue-500/10 hover:border-blue-100 active:scale-[0.98] ${!isSimulation && game ? 'cursor-pointer' : ''}`}
            >
                <div className="px-3 py-2 bg-slate-50/80 border-b border-slate-100/50 flex items-center justify-between">
                    {vGame?.id2 ? (
                        <div className="flex items-center gap-3 w-full">
                            <button onClick={(e) => handleLegClick(e, vGame.id1)} className="flex flex-col items-start hover:text-blue-600 transition-colors group/leg">
                                <span className="text-[7px] text-slate-400 font-black tracking-tighter mb-0.5 group-hover/leg:text-blue-400 uppercase">Leg 1</span>
                                <span className="text-[9px] font-bold text-slate-600 tabular-nums">{vGame.dateStr1}</span>
                            </button>
                            <div className="w-px h-5 bg-slate-200 shrink-0 rotate-12" />
                            <button onClick={(e) => handleLegClick(e, vGame.id2)} className="flex flex-col items-start hover:text-blue-600 transition-colors group/leg">
                                <span className="text-[7px] text-slate-400 font-black tracking-tighter mb-0.5 group-hover/leg:text-blue-400 uppercase">Leg 2</span>
                                <span className="text-[9px] font-bold text-slate-600 tabular-nums">{vGame.dateStr2}</span>
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between w-full">
                            <span className="text-[9px] font-extrabold text-slate-500 tabular-nums uppercase tracking-wide">{dateStr}</span>
                            <div className="flex items-center gap-1">
                                {isPEN && <span className="text-[7px] font-black px-1.5 py-0.5 rounded bg-amber-100 text-amber-600">PK</span>}
                                {isAET && !isPEN && <span className="text-[7px] font-black px-1.5 py-0.5 rounded bg-blue-50 text-blue-500">AET</span>}
                                {game && !isSimulation && <ChevronRight size={12} className="text-slate-300 group-hover:translate-x-0.5 transition-transform" />}
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-3.5 space-y-3">
                    {[team1, team2].map((team, idx) => (
                        <div key={idx} className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2.5 overflow-hidden flex-1">
                                <div className="w-6 h-6 shrink-0 flex items-center justify-center bg-white rounded-lg p-0.5 shadow-sm border border-slate-50 group-hover:scale-110 transition-transform duration-500">
                                    <img src={team?.logo || "/assets/ball.png"} className={`w-full h-full object-contain ${!team ? 'opacity-20 grayscale' : ''}`} alt="" />
                                </div>
                                <span className={`text-[11px] font-extrabold truncate tracking-tight transition-colors duration-300 ${team && winnerId === team.id ? 'text-slate-900' : 'text-slate-400'} ${!team ? 'opacity-30' : ''}`}>
                                    {team ? getTeamName(team.id, lang, team.name) : 'TBD'}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                {team?.score1 !== undefined && team?.score2 !== undefined && (
                                    <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-50 border border-slate-100/50 rounded-md text-[8px] font-bold text-slate-400 tabular-nums">
                                        <span className={team.score1 > team.scoreAway1! ? 'text-slate-600' : ''}>{team.score1}</span>
                                        <span className="text-[10px] opacity-20 font-light">•</span>
                                        <span className={team.score2 > team.scoreAway2! ? 'text-slate-600' : ''}>{team.score2}</span>
                                    </div>
                                )}
                                <div className="flex flex-col items-center min-w-[20px]">
                                    {team?.penalty !== undefined && (
                                        <span className="text-[7px] font-black text-amber-500 tabular-nums leading-none mb-0.5 animate-pulse">({team.penalty})</span>
                                    )}
                                    {team?.score !== undefined && (
                                        <span className={`text-[13px] font-black tabular-nums leading-none ${team && winnerId === team.id ? 'text-rose-500 drop-shadow-sm' : 'text-slate-300'}`}>
                                            {team.score}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    if (isWorldCup) {
        const wcRoundConfig: { key: typeof wcRound; label: string; count: string }[] = [
            { key: 'r32', label: '32강', count: `${bracketData.r32.length}경기` },
            { key: 'r16', label: '16강', count: `${bracketData.r16.filter(Boolean).length}경기` },
            { key: 'qf',  label: '8강',  count: `${bracketData.qf.filter(Boolean).length}경기` },
            { key: 'sf',  label: '4강',  count: `${bracketData.sf.filter(Boolean).length}경기` },
            { key: 'final', label: '결승', count: '' },
        ];

        const wcMatches: (Game | undefined)[] =
            wcRound === 'r32'   ? bracketData.r32 :
            wcRound === 'r16'   ? bracketData.r16 :
            wcRound === 'qf'    ? bracketData.qf  :
            wcRound === 'sf'    ? bracketData.sf  :
            [bracketData.thirdPlace, bracketData.final].filter(Boolean) as Game[];

        const validMatches = wcMatches.filter(Boolean) as Game[];

        return (
            <div className="w-full bg-white">
                {/* 라운드 선택 탭 */}
                <div className="flex gap-2 px-4 py-3 overflow-x-auto no-scrollbar border-b border-gray-100">
                    {wcRoundConfig.map(({ key, label, count }) => (
                        <button
                            key={key}
                            onClick={() => setWcRound(key)}
                            className={`shrink-0 flex flex-col items-center px-4 py-2 rounded-2xl text-[11px] font-black transition-all ${
                                wcRound === key
                                    ? 'bg-slate-900 text-white shadow-md'
                                    : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                            }`}
                        >
                            <span>{label}</span>
                            {count && <span className={`text-[8px] font-bold mt-0.5 ${wcRound === key ? 'text-white/60' : 'text-gray-300'}`}>{count}</span>}
                        </button>
                    ))}
                </div>

                {/* 결승 라운드: 3위전 + 결승 별도 표기 */}
                {wcRound === 'final' ? (
                    <div className="p-4 space-y-6">
                        {bracketData.thirdPlace && (
                            <div>
                                <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-3 px-1">3·4위전</div>
                                <div className="max-w-[200px]"><MatchCard game={bracketData.thirdPlace} /></div>
                            </div>
                        )}
                        {bracketData.final && (
                            <div>
                                <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-3 px-1 flex items-center gap-2">
                                    <Trophy size={12} className="text-yellow-500" /> 결승
                                </div>
                                <div className="max-w-[200px]"><MatchCard game={bracketData.final} /></div>
                                {(() => {
                                    const champion = getWinner(bracketData.final);
                                    if (!champion) return null;
                                    const vFinal = bracketData.final as VirtualGame;
                                    const wonByPK = (vFinal.penalty !== undefined && vFinal.penaltyAway !== undefined);
                                    const wonByAET = bracketData.final.statusShort === 'AET';
                                    return (
                                        <div className="mt-4 flex flex-col items-center gap-2 p-4 bg-gradient-to-b from-yellow-50 to-white rounded-2xl border border-yellow-100">
                                            <Trophy size={22} className="text-yellow-500 fill-yellow-400" />
                                            <div className="w-14 h-14 rounded-2xl bg-white border-2 border-yellow-200 shadow-md p-1.5">
                                                <img src={champion.logo} alt="" className="w-full h-full object-contain" />
                                            </div>
                                            <span className="text-base font-black text-slate-900 text-center leading-tight">
                                                {getTeamName(champion.id, lang, champion.name)}
                                            </span>
                                            <span className="text-[9px] font-black text-yellow-600 uppercase tracking-widest">2026 FIFA 월드컵 우승</span>
                                            {wonByPK && (
                                                <span className="text-[8px] font-bold text-amber-600 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">
                                                    승부차기 ({vFinal.penalty} : {vFinal.penaltyAway})
                                                </span>
                                            )}
                                            {wonByAET && !wonByPK && (
                                                <span className="text-[8px] font-bold text-blue-500 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full">
                                                    연장전 승
                                                </span>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                        {!bracketData.final && !bracketData.thirdPlace && (
                            <div className="flex flex-col items-center justify-center py-16 text-gray-300 gap-3">
                                <Trophy size={32} className="text-yellow-400 opacity-40" />
                                <span className="text-[10px] font-black uppercase tracking-widest">경기 일정 미정</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="p-4">
                        {validMatches.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 text-gray-300 gap-3">
                                <Trophy size={32} className="text-yellow-400 opacity-40" />
                                <span className="text-[10px] font-black uppercase tracking-widest">경기 일정 미정</span>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-3">
                                {validMatches.map((game) => (
                                    <MatchCard key={game.id} game={game} />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="relative w-full overflow-hidden bg-white rounded-3xl">
            {simulatedR16.length === 0 && bracketData.r16.length === 0 && (
                <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm p-6 text-center">
                    <div className="mb-6">
                        <Trophy size={48} className="text-yellow-500 mx-auto mb-4 animate-bounce" />
                        <h3 className="text-xl font-black text-slate-900 mb-2">{isUcl ? 'UCL 16강 대진표' : '대진표 정보'}</h3>
                        <p className="text-sm text-slate-500 max-w-xs mx-auto">조별 리그 결과를 바탕으로 대진표를 생성합니다.<br/>(같은 조 & 같은 국가 배제 규칙 적용)</p>
                    </div>
                    <button onClick={generateSimulatedBrackets} disabled={isLoading} className="flex items-center gap-2 px-8 py-3.5 bg-slate-900 text-white rounded-full font-bold hover:bg-slate-800 transition-all shadow-xl active:scale-95 disabled:opacity-50">
                        {isLoading ? <RefreshCw className="animate-spin" size={20} /> : <Trophy size={20} />} 16강 대진표 생성하기
                    </button>
                </div>
            )}
            
            <div className="relative overflow-x-auto custom-scrollbar">
                <div className="flex min-w-[1000px]">
                    <div className="flex items-center gap-12 p-6">
                        <div className="flex items-stretch gap-[40px]">
                            {/* R16 */}
                            <div className="flex flex-col justify-around min-h-[800px] space-y-4">
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <div key={i} className="relative">
                                        <MatchCard game={bracketData.r16[i]} />
                                        {/* Horizontal out */}
                                        <div className="absolute -right-5 top-1/2 -translate-y-1/2 w-5 h-[2px] bg-slate-200" />
                                        {/* Vertical rail */}
                                        <div className={`absolute -right-5 w-[2px] bg-slate-200 ${i % 2 === 0 ? 'top-1/2 h-[calc(50%+25px)]' : 'bottom-1/2 h-[calc(50%+25px)]'}`} />
                                        {/* Horizontal in to next round */}
                                        {i % 2 === 0 && (
                                            <div className="absolute -right-10 top-1/2 translate-y-[68px] w-5 h-[2px] bg-slate-200" />
                                        )}
                                    </div>
                                ))}
                            </div>
                            {/* QF */}
                            <div className="flex flex-col justify-around min-h-[800px] space-y-4">
                                {Array.from({ length: 4 }).map((_, i) => (
                                    <div key={i} className="relative">
                                        <MatchCard game={bracketData.qf[i]} />
                                        {/* Horizontal out */}
                                        <div className="absolute -right-5 top-1/2 -translate-y-1/2 w-5 h-[2px] bg-slate-200" />
                                        {/* Vertical rail */}
                                        <div className={`absolute -right-5 w-[2px] bg-slate-200 ${i % 2 === 0 ? 'top-1/2 h-[200px]' : 'bottom-1/2 h-[200px]'}`} />
                                        {/* Horizontal in */}
                                        {i % 2 === 0 && (
                                            <div className="absolute -right-10 top-1/2 translate-y-[136px] w-5 h-[2px] bg-slate-200" />
                                        )}
                                    </div>
                                ))}
                            </div>
                            {/* SF */}
                            <div className="flex flex-col justify-around min-h-[800px] space-y-4">
                                {Array.from({ length: 2 }).map((_, i) => (
                                    <div key={i} className="relative">
                                        <MatchCard game={bracketData.sf[i]} />
                                        {/* Horizontal out */}
                                        <div className="absolute -right-5 top-1/2 -translate-y-1/2 w-5 h-[2px] bg-slate-200" />
                                        {/* Vertical rail */}
                                        <div className={`absolute -right-5 w-[2px] bg-slate-200 ${i % 2 === 0 ? 'top-1/2 h-[300px]' : 'bottom-1/2 h-[300px]'}`} />
                                        {/* Horizontal in */}
                                        {i % 2 === 0 && (
                                            <div className="absolute -right-10 top-1/2 translate-y-[270px] w-5 h-[2px] bg-slate-200" />
                                        )}
                                    </div>
                                ))}
                            </div>
                            {/* Final */}
                            <div className="flex flex-col justify-around min-h-[800px] space-y-4">
                                <div className="relative">
                                    <MatchCard game={bracketData.final} />
                                    <div className="absolute -right-10 top-1/2 -translate-y-1/2 w-10 h-[2px] bg-slate-200" />
                                </div>
                            </div>
                            {/* Winner Placeholder for Line */}
                            <div className="flex flex-col justify-around h-[640px] -ml-10">
                                <div className="w-10" />
                            </div>
                        </div>

                        <div className="flex flex-col items-center justify-center min-w-[200px]">
                            <div className="relative mb-12 group">
                                <div className="absolute inset-0 bg-yellow-400/10 blur-3xl rounded-full scale-150" />
                                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white px-5 py-2 rounded-2xl shadow-xl border border-yellow-100 flex items-center gap-2 z-20 whitespace-nowrap">
                                    <Trophy size={14} className="text-yellow-500 fill-yellow-500" />
                                    <span className="text-[10px] font-black text-slate-900 tracking-widest uppercase">Champions</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes float { 0%, 100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-12px) rotate(1deg); } }
                .animate-float { animation: float 4s ease-in-out infinite; }
                .custom-scrollbar::-webkit-scrollbar { height: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #E2E8F0; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #CBD5E1; }
            `}</style>
        </div>
    );
};
